/**
 * <DataTable> — headless table shell.
 *
 * Phase 0 ships the *shell* only — CVA-typed density (`comfortable` /
 * `compact`), selection-row visual, logical-property borders. Plan 3 wires
 * TanStack Table v8 on top.
 *
 * UI-SPEC §Color rule 5 (Primary reserved for): selected rows render with a
 * primary logical-start border + soft-primary tint. Logical `border-s-*`
 * auto-mirrors to the right edge in RTL via Tailwind v4's logical property
 * support — zero JS branching needed.
 *
 * UI-SPEC §Typography: `tabular-nums` on every cell so amount/balance columns
 * don't jitter between rows.
 *
 * CONTEXT D-02: `comfortable` is the default density; `compact` is opt-in for
 * high-volume HR Admin tables (employee list, attendance log).
 */
import * as React from "react";
import { cn, cva, type VariantProps } from "../../utils";
import type { NonEmptyString } from "../../types/NonEmptyString";
import { useListStagger } from "../../hooks/useListStagger";

// ---------------------------------------------------------------------------
// Density variants
// ---------------------------------------------------------------------------

/**
 * reference design `app.css §.table` calibration (2026-04-25):
 *  - body font-size 13px (`text-[13px]`)
 *  - cells use border-spacing:0 + only border-bottom (no double inner lines)
 *  - row height drives off `--size-row-h` (48 comfortable / 38 compact via
 *    density.css), so cells use `h-[var(--size-row-h)]` rather than padded
 *    block sizing
 *  - horizontal padding 14px (between Tailwind `md`=12 and `lg`=16, hence
 *    arbitrary value)
 *  - thead is paper-alt, font-weight 500, 11.5px uppercase, `tracking-wider`
 *    (see DataTableHeader render)
 */
export const dataTableVariants = cva(
  cn(
    "w-full border-separate border-spacing-0",
    "bg-surface-elevated",
    "tabular-nums",
    "text-[13px] text-default",
  ),
);

export const dataTableCellVariants = cva(
  cn("align-middle text-start border-b border-subtle", "px-[14px] h-[var(--size-row-h)]"),
  {
    variants: {
      density: {
        // Row height is now driven by --size-row-h (48 comfortable, 38 compact
        // via density.css) — the variant only differentiates if a future
        // override is needed. No padding-y because the height fully drives
        // vertical centering via vertical-align: middle.
        comfortable: "",
        compact: "",
      },
    },
    defaultVariants: { density: "comfortable" },
  },
);

export type DataTableDensity = NonNullable<VariantProps<typeof dataTableCellVariants>["density"]>;

// ---------------------------------------------------------------------------
// Context — density cascades from the root table to cells.
// ---------------------------------------------------------------------------

interface DataTableCtx {
  density: DataTableDensity;
}

const DataTableContext = React.createContext<DataTableCtx>({
  density: "comfortable",
});

// ---------------------------------------------------------------------------
// <DataTable> — root <table>
// ---------------------------------------------------------------------------

export interface DataTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Density variant — `comfortable` (default) or `compact`. */
  density?: DataTableDensity;
}

export const DataTable = React.forwardRef<HTMLTableElement, DataTableProps>(function DataTable(
  { density = "comfortable", className, children, ...rest },
  ref,
) {
  const ctx = React.useMemo<DataTableCtx>(() => ({ density }), [density]);
  return (
    <DataTableContext.Provider value={ctx}>
      <table ref={ref} className={cn(dataTableVariants(), className)} {...rest}>
        {children}
      </table>
    </DataTableContext.Provider>
  );
});

// ---------------------------------------------------------------------------
// Canonical header/cell class constants (X-3 convergence)
// ---------------------------------------------------------------------------

/**
 * Canonical `<thead>` treatment (reference design §.table th): paper-alt bg,
 * font-weight 500, 11.5px uppercase tracked muted text. Sticky so
 * virtualized scroll containers keep the header pinned. The tracking is the
 * theme's `tracking-wider` (not a literal em value) so Arabic headers drop it
 * with every other label — see the `:lang(ar)` block in typography.css.
 *
 * Exported so hand-rolled `<table>`s (payroll, settings, team screens that
 * don't sit on the full `DataTable` shell) can converge on the same header
 * look without re-deriving the class string. Apply to a bare `<thead>`.
 */
export const tableHeaderClass = cn(
  "sticky top-0 z-[1]",
  "bg-surface-sunken text-muted",
  "text-[11.5px] font-medium uppercase tracking-wider",
);

/**
 * Canonical `<th>` cell treatment for hand-rolled tables. Start-aligned,
 * bottom-bordered, comfortable padding. Add `text-end` for numeric columns.
 */
export const tableHeadCellClass = cn(
  "align-middle text-start border-b border-subtle",
  "px-[14px] py-sm font-semibold",
);

/**
 * Canonical `<tr>` body-row treatment for hand-rolled tables: bottom border
 * (dropped on the last row) + the same hover tint DataTableRow uses.
 */
export const tableBodyRowClass = cn(
  "border-b border-subtle last:border-b-0",
  "transition-colors duration-fast hover:bg-surface-sunken",
);

/**
 * Canonical `<td>` body-cell treatment for hand-rolled tables. Add `text-end`
 * + `tabular-nums` for numeric/money columns.
 */
export const tableBodyCellClass = cn("align-middle px-[14px] py-sm");

// ---------------------------------------------------------------------------
// <DataTableHeader> — <thead>
// ---------------------------------------------------------------------------

export const DataTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(function DataTableHeader({ className, ...rest }, ref) {
  return <thead ref={ref} className={cn(tableHeaderClass, className)} {...rest} />;
});

// ---------------------------------------------------------------------------
// <DataTableBody> — <tbody>
// ---------------------------------------------------------------------------

export interface DataTableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  /**
   * Opt-in first-mount row stagger (WS-1 motion layer). When true, each direct
   * `<tr>` child fades+rises in sequence (30ms/row, capped at 8) the FIRST time
   * the body renders; refetches / re-renders do NOT re-animate. Reduced-motion
   * collapses to an instant fade (handled in motion-utilities.css). Off by
   * default so high-churn tables opt in deliberately.
   */
  stagger?: boolean;
}

export const DataTableBody = React.forwardRef<HTMLTableSectionElement, DataTableBodyProps>(
  function DataTableBody({ className, stagger = false, children, ...rest }, ref) {
    const { getStaggerProps } = useListStagger(stagger, React.Children.count(children) > 0);
    // Only touch children when staggering — otherwise pass them through
    // untouched (zero overhead, no key/clone churn for the common case).
    const staggered =
      stagger && children !== undefined && children !== null
        ? React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) return child;
            const { className: rowStagger, style } = getStaggerProps(index);
            const el = child as React.ReactElement<{
              className?: string;
              style?: React.CSSProperties;
            }>;
            return React.cloneElement(el, {
              className: cn(el.props.className, rowStagger),
              style: { ...el.props.style, ...style },
            });
          })
        : children;
    return (
      <tbody
        ref={ref}
        // reference design app.css §.table — last row drops its border-bottom so
        // any card-foot underneath isn't doubled up with the table's own line.
        className={cn("[&>tr:last-child>td]:border-b-0 [&>tr:last-child>th]:border-b-0", className)}
        {...rest}
      >
        {staggered}
      </tbody>
    );
  },
);

// ---------------------------------------------------------------------------
// <DataTableRow> — <tr> with selection state (UI-SPEC Primary rule 5)
// ---------------------------------------------------------------------------

export interface DataTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /**
   * Selected rows render a primary-color logical-start border + soft-primary
   * tint. `border-s-*` auto-mirrors to the right edge in RTL.
   */
  selected?: boolean;
}

export const DataTableRow = React.forwardRef<HTMLTableRowElement, DataTableRowProps>(
  function DataTableRow({ selected, className, ...rest }, ref) {
    return (
      <tr
        ref={ref}
        data-selected={selected ? "true" : undefined}
        className={cn(
          "transition-colors duration-fast",
          "hover:bg-surface-sunken",
          // UI-SPEC Primary rule 5: selected state — logical start border +
          // soft tint. Mirrors to the right edge in RTL automatically.
          "data-[selected=true]:bg-primary-soft",
          "data-[selected=true]:border-s-2",
          "data-[selected=true]:border-s-primary",
          className,
        )}
        {...rest}
      />
    );
  },
);

// ---------------------------------------------------------------------------
// <DataTableHead> — <th>
// ---------------------------------------------------------------------------

export const DataTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(function DataTableHead({ className, scope = "col", ...rest }, ref) {
  const { density } = React.useContext(DataTableContext);
  return (
    <th
      ref={ref}
      scope={scope}
      className={cn(dataTableCellVariants({ density }), "font-semibold", className)}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// <DataTableCell> — <td>
// ---------------------------------------------------------------------------

export const DataTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(function DataTableCell({ className, ...rest }, ref) {
  const { density } = React.useContext(DataTableContext);
  return <td ref={ref} className={cn(dataTableCellVariants({ density }), className)} {...rest} />;
});

// ---------------------------------------------------------------------------
// <TableSkeleton> — per-row loading placeholder (X-10 convergence)
// ---------------------------------------------------------------------------
//
// LOADING-STATE RULE (X-10): use a *skeleton* for content-shaped regions that
// take >200ms to load (tables, cards, detail panels); use a *spinner* only for
// inline/button actions or indeterminate background ops <200ms. Skeletons
// preserve layout and reduce perceived jank; spinners are for point actions.
//
// A11y (X-5): a table skeleton is a SINGLE `role="status"` region with ONE
// localized `aria-label` on the surrounding `<tbody>`. The individual cell
// bars are decorative (`aria-hidden`) — never emit chatty per-cell
// "Loading row N avatar" labels. Screen readers announce one "Loading …".

/** A shimmering placeholder bar — decorative, `aria-hidden`. */
function SkeletonBar({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "bg-surface-sunken rounded-md animate-pulse motion-reduce:animate-none",
        "h-4 w-full max-w-[7rem]",
        className,
      )}
    />
  );
}

export interface TableSkeletonProps {
  /** Number of placeholder rows to render. Default 5. */
  rows?: number;
  /** Number of columns per row. */
  columns: number;
  /**
   * One localized busy label for the whole skeleton region (e.g.
   * `t("common:loading.aria", { entity })`). Applied once to the `<tbody>`;
   * the per-cell bars are `aria-hidden`.
   */
  "aria-label": NonEmptyString;
  /**
   * Optional per-column extra classes (e.g. `["", "flex justify-end"]` to
   * right-align a numeric column's bar). Indexed by column position.
   */
  columnClassNames?: readonly string[];
}

/**
 * Renders `rows × columns` decorative skeleton cells inside a labelled
 * `<tbody role="status">`. Drop it in where a `<DataTableBody>` would go while
 * data is loading, so hand-built per-row skeletons stop being copy-pasted.
 */
export function TableSkeleton({
  rows = 5,
  columns,
  "aria-label": ariaLabel,
  columnClassNames,
}: TableSkeletonProps): React.JSX.Element {
  const { density } = React.useContext(DataTableContext);
  return (
    <tbody role="status" aria-busy="true" aria-live="polite" aria-label={ariaLabel}>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <td key={c} className={cn(dataTableCellVariants({ density }))}>
              <div className={cn("flex", columnClassNames?.[c])}>
                <SkeletonBar />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
